import { ProcedureDocumentType, InstructionType, Fasting, OrderStatus, OrderFormat, OfficeType, Role, OrderParty, UserPlaceRole } from '@caresend/types';
import { initOrderFlowData, isNullish, generateID, initCCAccount, initBooking, initElementTimestamp, initDraftOrder, deduplicateArrayByProperty, updateOrAddToArray, nullishFilter, getCountryISOByLocation, hasStatus, initDraftProcedure, stripAlgoliaProperties, getIntersection, deduplicateArray, initExistingPatientForEditing, arrayToObj } from '@caresend/utils';
import update from 'immutability-helper';
import cloneDeep from 'lodash.clonedeep';
import { computed } from 'vue';
import '../../../database/algoliaConfig/indices.js';
import { needsNewKey, searchICD10Codes, searchUsers, searchProducts } from '../../../database/algoliaConfig/methods.js';
import { processCSVInput } from '../../../components/specific/orderFlow/CSVOrderInput/processor.js';
import { ActionRequiredFilter } from '../../../components/specific/VisitsPage/subComponents/OrdersVisitsFilters/model.js';
import { decryptFile, decryptData } from '../../../database/encryption/decrypt.js';
import { encryptData } from '../../../database/encryption/encrypt.js';
import '../../../database/encryption/model.js';
import 'js-sha256';
import { getOrderFlowStateInfoRequest, initNewDraftOrderFromOrderRequest, updateUserInfoRequest, getAlgoliaAPIKeyForICD10CodesRequest, getAlgoliaAPIKeyForSearchPrescribersRequest, getAlgoliaAPIKeyForProductsRequest } from '../../../database/firebaseConfig/cloudFunctions.js';
import '@sentry/vue';
import { toastErrorAndReport, toastInfo } from '../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import { pushAndKeepQuery, keepParams, keepQuery } from '../../../router/helpers/builders.js';
import { getRouter, getRoute } from '../../../router/helpers/initRouter.js';
import '../../../router/routes/dev/model.js';
import '../../../router/routes/dev/routes.js';
import { homeRoute } from '../../../router/routes/locations.js';
import { orderFlowSwitchFormatRoute } from '../../../router/routes/orderFlow/locations.js';
import '../../../router/routes/orderFlow/model.js';
import '../../../router/routes/orderFlow/routes.js';
import '../../../router/routes/schedulingFlow/model.js';
import '../../../router/routes/schedulingFlow/routes.js';
import '../../../router/routes/chartingFlow/model.js';
import '../../../router/routes/chartingFlow/routes.js';
import '../../../router/routes/portal/portalRoutes.js';
import '../../../router/routes/portal/model.js';
import '../../../router/routes/pro/model.js';
import '../../../router/routes/visitsPage/model.js';
import '../../../router/routes/visitsPage/routes.js';
import { isDashboardUser, dispatchOrThrow, hasMutation, customCommit, isLabOfficeUser } from '../../helpers.js';
import { initExtendedModule } from '../helpers.js';
import { isOfficeLab } from '../office/init.js';
import { saveOrderAsDraft, createOrder, mergeBookingProceduresByProducts, splitBookingProceduresByProducts } from './helpers.js';
import { saveOrder } from './orderFlowSteps/completionActions.js';
import { getOrderFlowHelpers } from './orderFlowSteps/steps.js';
import { setOfficeLocalOrderID } from '../../set/office.js';

const initialLocalState = (isUnsaved, 
/**
 * Undefined for the module init, but must be passed any time the order
 * flow component mounts.
 */
draftOrderID) => {
    const flowDataLoadingInit = draftOrderID ? { [draftOrderID]: false } : {};
    return {
        diagnosesKeyData: null,
        prescriberSearchKeyData: null,
        flowAlertData: null,
        flowDataIsReady: flowDataLoadingInit,
        flowLoadingCompleted: flowDataLoadingInit,
        flowLoadingError: null,
        isUnsaved,
        loading: {
            isLoading: false,
        },
        locallyAddedPrescribersByOfficeID: {},
        nextButtonLoading: false,
        officeDependenciesLoading: false,
        orderCsvImportLogs: [],
        patientAlreadyHasTheKit: false,
        testsKeyData: null,
    };
};
const orderFlowState = {
    data: initOrderFlowData(),
    draftBookings: undefined,
    draftProcedures: undefined,
    draftWaypoints: undefined,
    draftOrder: undefined,
    id: undefined,
    localState: initialLocalState(true),
};
const orderFlowMutations = {
    'orderFlow/ADD_CC_ACCOUNT': (state, { procedureID }) => {
        var _a;
        const setCCAccountError = new Error('Unable to set account. Please try again.');
        const procedure = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID];
        if (isNullish(procedure))
            throw setCCAccountError;
        const ccAccountID = generateID();
        procedure.ccAccounts = {
            ...procedure.ccAccounts,
            [ccAccountID]: initCCAccount(ccAccountID),
        };
        state.draftProcedures = {
            ...state.draftProcedures,
            [procedureID]: procedure,
        };
    },
    'orderFlow/ADD_EMPTY_PATIENT_BOOKING': (state, reset = false) => {
        var _a;
        if (isNullish(state.draftOrder))
            return;
        const booking = initBooking(state.draftOrder.id);
        state.draftBookings = update(reset ? {} : state.draftBookings, {
            [booking.id]: { $set: booking },
        });
        state.draftOrder = update(state.draftOrder, {
            bookings: {
                $set: update((_a = state.draftOrder.bookings) !== null && _a !== void 0 ? _a : {}, {
                    [booking.id]: { $set: initElementTimestamp(booking.id) },
                }),
            },
        });
    },
    'orderFlow/ADD_PATIENT_BOOKING': (state, booking) => {
        var _a;
        if (isNullish(state.draftOrder))
            return;
        state.draftBookings = update(state.draftBookings, {
            [booking.id]: { $set: booking },
        });
        state.draftOrder = update(state.draftOrder, {
            bookings: {
                $set: update((_a = state.draftOrder.bookings) !== null && _a !== void 0 ? _a : {}, {
                    [booking.id]: { $set: initElementTimestamp(booking.id) },
                }),
            },
        });
    },
    'orderFlow/ADD_PROCEDURE': (state, { bookingID, newItem }) => {
        var _a, _b, _c, _d;
        const booking = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
        if (isNullish(booking))
            throw new Error(`Unable to add procedure to booking ${bookingID}`);
        state.draftBookings = update(state.draftBookings, {
            [bookingID]: {
                procedures: {
                    $set: update((_d = (_c = (_b = state.draftBookings) === null || _b === void 0 ? void 0 : _b[bookingID]) === null || _c === void 0 ? void 0 : _c.procedures) !== null && _d !== void 0 ? _d : {}, {
                        [newItem.id]: { $set: initElementTimestamp(newItem.id) },
                    }),
                },
            },
        });
        state.draftProcedures = update(state.draftProcedures, {
            [newItem.id]: { $set: newItem },
        });
    },
    'orderFlow/INIT_DRAFT_ORDER': (state, { id, creatorID, officeID }) => {
        state.draftOrder = initDraftOrder(id, creatorID, officeID);
    },
    'orderFlow/INIT_RE_ORDER': (state, orderFlowStateInfo) => {
        state.data = orderFlowStateInfo.data;
        state.draftBookings = orderFlowStateInfo.draftBookings;
        state.draftOrder = orderFlowStateInfo.draftOrder;
        state.draftProcedures = orderFlowStateInfo.draftProcedures;
        state.id = orderFlowStateInfo.id;
    },
    'orderFlow/SET_IS_CSV_IMPORT': (state, isCSVImport) => {
        state.isCsvImport = isCSVImport;
    },
    'orderFlow/REMOVE_BOOKING_PATIENT': (state, bookingID) => {
        state.draftBookings = update(state.draftBookings, {
            [bookingID]: { $unset: ['patientID'] },
        });
    },
    'orderFlow/REMOVE_CC_ACCOUNT': (state, { procedureID, ccAccountID }) => {
        var _a;
        const procedure = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID];
        if (isNullish(procedure))
            throw Error('Unable to remove account. Please try again.');
        state.draftProcedures = update(state.draftProcedures, {
            [procedureID]: {
                ccAccounts: { $unset: [ccAccountID] },
            },
        });
    },
    /**
     * Use `orderFlow/removePatientBooking` action to avoid runtime errors.
     */
    'orderFlow/REMOVE_PATIENT_BOOKING': (state, bookingID) => {
        var _a, _b, _c;
        if (!state.draftOrder)
            return;
        state.draftOrder = update(state.draftOrder, {
            bookings: { $unset: [bookingID] },
        });
        const procedureIDsOnBooking = Object.keys((_c = (_b = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID]) === null || _b === void 0 ? void 0 : _b.procedures) !== null && _c !== void 0 ? _c : {});
        procedureIDsOnBooking.forEach((procedureID) => {
            state.draftProcedures = update(state.draftProcedures, {
                $unset: [procedureID],
            });
        });
        state.draftBookings = update(state.draftBookings, {
            $unset: [bookingID],
        });
    },
    'orderFlow/REMOVE_PLACE_ACCOUNT': (state, { procedureID }) => {
        var _a;
        const removePlaceAccountError = new Error('Unable to remove account. Please try again.');
        const procedure = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID];
        if (isNullish(procedure))
            throw removePlaceAccountError;
        delete procedure.placeAccounts;
        delete procedure.dropOffPlaceGroups;
        state.draftProcedures = {
            ...state.draftProcedures,
            [procedureID]: procedure,
        };
    },
    'orderFlow/RESET_BOOKING_DOCUMENTS': (state, bookingID) => {
        var _a;
        const booking = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
        if (!booking)
            return;
        booking.documents = undefined;
    },
    'orderFlow/RESET_BOOKING_PROCEDURES': (state, bookingID) => {
        var _a, _b;
        const booking = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
        if (!booking)
            return;
        Object.values((_b = booking.procedures) !== null && _b !== void 0 ? _b : {}).forEach((procedure) => {
            if (!state.draftProcedures)
                return;
            delete state.draftProcedures[procedure.id];
        });
        booking.procedures = undefined;
    },
    'orderFlow/RESET_ALL_BOOKING_PROCEDURES': (state) => {
        var _a;
        Object.values((_a = state.draftBookings) !== null && _a !== void 0 ? _a : {}).forEach((booking) => {
            if (!booking.procedures)
                return;
            Object.values(booking.procedures).forEach((procedure) => {
                if (!state.draftProcedures)
                    return;
                delete state.draftProcedures[procedure.id];
            });
            state.draftBookings = update(state.draftBookings, {
                [booking.id]: { procedures: { $set: undefined } },
            });
        });
    },
    'orderFlow/RESET_FLOW_ALERT_DATA': (state) => {
        state.localState.flowAlertData = null;
    },
    'orderFlow/RESET_LOCAL_STATE': (state, { draftOrderID, isNew }) => {
        state.localState = initialLocalState(isNew, draftOrderID);
    },
    'orderFlow/RESET_MODULE': (state) => {
        state.data = initOrderFlowData();
        state.draftBookings = {};
        state.draftProcedures = {};
        state.draftWaypoints = undefined;
        state.draftOrder = undefined;
        state.id = undefined;
    },
    'orderFlow/SET_ALGOLIA_DIAGNOSES_KEY': (state, keyData) => {
        state.localState.diagnosesKeyData = keyData;
    },
    'orderFlow/SET_ALGOLIA_PRESCRIBER_SEARCH_KEY': (state, keyData) => {
        state.localState.prescriberSearchKeyData = keyData;
    },
    'orderFlow/SET_ALGOLIA_TESTS_KEY': (state, keyData) => {
        state.localState.testsKeyData = keyData;
    },
    'orderFlow/SET_BOOKING_DOCUMENTS': (state, { bookingID, files, type }) => {
        var _a, _b, _c;
        const setDocumentError = new Error('Unable to set document. Please try again.');
        const booking = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
        if (isNullish(booking))
            throw setDocumentError;
        const filteredDocuments = (_c = (_b = booking === null || booking === void 0 ? void 0 : booking.documents) === null || _b === void 0 ? void 0 : _b.filter((document) => document.type !== type)) !== null && _c !== void 0 ? _c : [];
        const newDocuments = files.map((file) => ({
            file,
            id: generateID(),
            type: type !== null && type !== void 0 ? type : ProcedureDocumentType.GENERIC_DOCUMENTS,
        }));
        const documents = [...filteredDocuments, ...newDocuments];
        state.draftBookings = update(state.draftBookings, {
            [bookingID]: {
                documents: { $set: documents },
            },
        });
        if (booking.procedures) {
            Object.keys(booking.procedures).forEach((procedureID) => {
                var _a, _b, _c;
                const procedure = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID];
                if (isNullish(procedure))
                    return;
                const procedureDocuments = (_c = (_b = procedure === null || procedure === void 0 ? void 0 : procedure.documents) === null || _b === void 0 ? void 0 : _b.filter((document) => document.type !== type)) !== null && _c !== void 0 ? _c : [];
                procedure.documents = [...procedureDocuments, ...newDocuments];
            });
        }
    },
    'orderFlow/SET_BOOKING_LAB_ORDER_ID': (state, { bookingID, labOrderID, }) => {
        var _a;
        if (!((_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID]))
            return;
        state.draftBookings = update(state.draftBookings, {
            [bookingID]: {
                labOrderID: { $set: labOrderID },
            },
        });
    },
    'orderFlow/SET_BOOKING_PATIENT': (state, { bookingID, patientID }) => {
        state.draftBookings = update(state.draftBookings, {
            [bookingID]: { patientID: { $set: patientID !== null && patientID !== void 0 ? patientID : undefined } },
        });
    },
    'orderFlow/SET_BOOKING_PATIENT_SHIPMENT_TRACKING_NUMBER': (state, { bookingID, patientShipmentTrackingNumber, }) => {
        var _a;
        if (!((_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID]))
            return;
        state.draftBookings = update(state.draftBookings, {
            [bookingID]: {
                patientShipmentTrackingNumber: { $set: patientShipmentTrackingNumber },
            },
        });
    },
    'orderFlow/SET_BOOKING_SAMPLE_SHIPMENT_TRACKING_NUMBER': (state, { bookingID, sampleShipmentTrackingNumber, }) => {
        var _a;
        if (!((_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID]))
            return;
        state.draftBookings = update(state.draftBookings, {
            [bookingID]: { sampleShipmentTrackingNumber: { $set: sampleShipmentTrackingNumber } },
        });
    },
    'orderFlow/SET_CC_ACCOUNT': (state, { procedureID, ccAccount }) => {
        var _a, _b, _c, _d;
        const procedure = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID];
        if (isNullish(procedure))
            throw Error('Unable to set account. Please try again.');
        state.draftProcedures = update(state.draftProcedures, {
            [procedureID]: {
                ccAccounts: {
                    $set: update((_d = (_c = (_b = state.draftProcedures) === null || _b === void 0 ? void 0 : _b[procedureID]) === null || _c === void 0 ? void 0 : _c.ccAccounts) !== null && _d !== void 0 ? _d : {}, {
                        [ccAccount.id]: { $set: ccAccount },
                    }),
                },
            },
        });
    },
    'orderFlow/SET_CREATED_BY': (state, userID) => {
        if (isNullish(state.draftOrder))
            return;
        state.draftOrder = update(state.draftOrder, {
            createdBy: { $set: userID },
        });
    },
    'orderFlow/SET_CREATED_BY_OFFICE_ID': (state, officeID) => {
        if (isNullish(state.draftOrder))
            return;
        state.draftOrder = update(state.draftOrder, {
            createdByOfficeID: { $set: officeID },
        });
    },
    'orderFlow/SET_DIAGNOSES_ON_PROCEDURE': (state, { procedureID, diagnoses }) => {
        var _a;
        const procedure = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID];
        if (isNullish(procedure))
            return;
        state.draftProcedures = update(state.draftProcedures, {
            [procedureID]: {
                diagnoses: { $set: diagnoses },
            },
        });
    },
    'orderFlow/SET_DRAW_PLACE_ID': (state, placeID) => {
        if (!state.draftOrder)
            throw Error('Missing draft order.');
        state.draftOrder = update(state.draftOrder, {
            drawPlaceID: { $set: placeID },
        });
    },
    'orderFlow/SET_EXISTING_DRAFT_ORDER': (state, newState) => {
        var _a, _b;
        state.data = newState.data;
        state.draftBookings = (_a = newState.draftBookings) !== null && _a !== void 0 ? _a : {};
        state.draftProcedures = (_b = newState.draftProcedures) !== null && _b !== void 0 ? _b : {};
        state.draftWaypoints = newState.draftWaypoints;
        state.draftOrder = newState.draftOrder;
        state.id = newState.id;
    },
    'orderFlow/SET_FLOW_ALERT_DATA': (state, flowAlertData) => {
        state.localState.flowAlertData = flowAlertData;
    },
    'orderFlow/SET_FLOW_DATA_IS_READY': (state, { draftOrderID, ready }) => {
        state.localState.flowDataIsReady = update(state.localState.flowDataIsReady, { [draftOrderID]: { $set: ready } });
    },
    'orderFlow/SET_FLOW_LOADING_COMPLETED': (state, { draftOrderID, completed }) => {
        if (!completed) {
            state.localState.flowDataIsReady = update(state.localState.flowDataIsReady, { [draftOrderID]: { $set: false } });
        }
        state.localState.flowLoadingCompleted = update(state.localState.flowLoadingCompleted, { [draftOrderID]: { $set: completed } });
    },
    'orderFlow/SET_FLOW_LOADING_ERROR': (state, message) => {
        state.localState.flowLoadingError = message;
    },
    'orderFlow/SET_LOADING': (state, loadingData) => {
        state.localState.loading = update(state.localState.loading, { $set: loadingData });
    },
    'orderFlow/SET_LOCALLY_ADDED_PRESCRIBER_BY_OFFICE_ID': (state, { prescriber, officeID }) => {
        var _a;
        const prescribersOnOffice = (_a = state.localState.locallyAddedPrescribersByOfficeID[officeID]) !== null && _a !== void 0 ? _a : [];
        const updatedPrescribers = deduplicateArrayByProperty([prescriber, ...prescribersOnOffice], 'id');
        state.localState.locallyAddedPrescribersByOfficeID = update(state.localState.locallyAddedPrescribersByOfficeID, { [officeID]: { $set: updatedPrescribers } });
    },
    'orderFlow/SET_FLOW_SETTINGS': (state, flowSettings) => {
        if (isNullish(state.draftOrder))
            return;
        state.data.flowSettings = {
            ...state.data.flowSettings,
            ...flowSettings,
        };
        state.draftOrder.flowData = state.data;
    },
    'orderFlow/SET_INSTRUCTIONS': (state, { procedureID, instructionUpdate, }) => {
        var _a;
        const setInstructionError = new Error('Unable to set instruction. Please try again.');
        const procedure = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID];
        if (isNullish(procedure))
            throw setInstructionError;
        const newInstructions = updateOrAddToArray(procedure.instructions, (instruction) => instruction.type === instructionUpdate.type, (instruction) => ({
            instruction: '',
            ...instruction,
            ...instructionUpdate,
        }));
        if (newInstructions) {
            state.draftProcedures = update(state.draftProcedures, {
                [procedureID]: {
                    instructions: { $set: newInstructions },
                },
            });
        }
    },
    'orderFlow/SET_NEXT_BUTTON_LOADING': (state, isLoading) => {
        state.localState.nextButtonLoading = isLoading;
    },
    'orderFlow/SET_OFFLINE_SCHEDULING': (state, offlineScheduling) => {
        if (isNullish(state.draftOrder))
            return;
        state.draftOrder = update(state.draftOrder, {
            offlineScheduling: { $set: offlineScheduling },
        });
    },
    'orderFlow/SET_ORDER_CSV_IMPORT_FILE': (state, csvImportFile) => {
        if (!state.draftOrder)
            throw new Error('Unable to set csv import file. Please try again.');
        state.draftOrder.csvImportFile = csvImportFile;
    },
    'orderFlow/APPEND_ORDER_CSV_IMPORT_LOG_HEADER': (state, log) => {
        state.localState.orderCsvImportLogs.push({
            message: log,
            timestamp: Date.now(),
            type: 'header',
        });
    },
    'orderFlow/APPEND_ORDER_CSV_IMPORT_LOG': (state, log) => {
        state.localState.orderCsvImportLogs.push({
            message: log,
            timestamp: Date.now(),
            type: 'log',
            status: 'inProgress',
        });
    },
    'orderFlow/SET_CURRENT_CSV_IMPORT_LOG_STATUS': (state, { status }) => {
        const index = state.localState.orderCsvImportLogs.length - 1;
        const log = state.localState.orderCsvImportLogs[index];
        if (!log)
            return;
        state.localState.orderCsvImportLogs[index] = {
            ...log,
            status,
        };
    },
    'orderFlow/SET_OFFICE_DEPENDENCIES_LOADING': (state, isLoading) => {
        state.localState.officeDependenciesLoading = isLoading;
    },
    'orderFlow/SET_ORDER_FORMAT_BY_BOOKING_ID': (state, { bookingID, orderFormat }) => {
        state.data.flowSettings = {
            ...state.data.flowSettings,
            orderFormatByBookingID: {
                ...state.data.flowSettings.orderFormatByBookingID,
                [bookingID]: orderFormat,
            },
        };
    },
    'orderFlow/SET_ORDER_FULFILLER': (state, isFulfilledByOffice) => {
        if (isNullish(state.draftOrder))
            return;
        state.draftOrder = update(state.draftOrder, {
            isFulfilledByOffice: { $set: isFulfilledByOffice },
        });
    },
    'orderFlow/SET_ORDER_OFFICE': (state, { officeID }) => {
        if (!state.draftOrder)
            throw new Error('Unable to set office. Please try again.');
        state.draftOrder = update(state.draftOrder, {
            officeID: { $set: officeID !== null && officeID !== void 0 ? officeID : undefined },
        });
    },
    'orderFlow/SET_PATIENT_ON_BOOKING': (state, { bookingID, patient }) => {
        var _a;
        const booking = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
        if (isNullish(patient) || isNullish(booking))
            return;
        state.draftBookings = update(state.draftBookings, {
            [bookingID]: {
                patientID: { $set: patient.id },
            },
        });
    },
    'orderFlow/SET_PATIENT_ALREADY_HAS_THE_KIT': (state, hasKit) => {
        state.localState.patientAlreadyHasTheKit = hasKit;
    },
    'orderFlow/SET_PLACE_ACCOUNT': (state, { procedureID, placeAccount }) => {
        var _a, _b, _c;
        const setPlaceAccountError = new Error('Unable to set account. Please try again.');
        const bookingID = (_b = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID]) === null || _b === void 0 ? void 0 : _b.bookingID;
        if (isNullish(bookingID))
            throw setPlaceAccountError;
        state.draftProcedures = update((_c = state.draftProcedures) !== null && _c !== void 0 ? _c : {}, {
            $apply: (procedures) => Object.values(procedures).reduce((acc, procedure) => {
                if (procedure.bookingID !== bookingID)
                    return acc;
                let { placeGroupID } = placeAccount;
                if (placeAccount.privatePlaceGroup) {
                    placeGroupID = placeAccount.privatePlaceGroup.id;
                }
                if (!placeGroupID)
                    throw setPlaceAccountError;
                const newPlaceAccounts = { $set: {
                        [placeAccount.id]: {
                            procedureID: procedure.id,
                            id: placeAccount.id,
                        },
                    } };
                const newDropOffPlaceGroups = { $set: {
                        [placeGroupID]: initElementTimestamp(placeGroupID),
                    } };
                return update(acc, {
                    [procedure.id]: { $set: update(procedure, {
                            placeAccounts: newPlaceAccounts,
                            dropOffPlaceGroups: newDropOffPlaceGroups,
                        }) },
                });
            }, procedures),
        });
    },
    'orderFlow/SET_PRESCRIBER_ID_ON_ORDER': (state, prescriberID) => {
        if (isNullish(state.draftOrder) || !prescriberID)
            return;
        state.draftOrder = update(state.draftOrder, {
            prescriberID: { $set: prescriberID },
        });
    },
    /**
     * If no procedure ID is provided, it's assumed that any changes will affect all procedures
     */
    'orderFlow/SET_PROCEDURE_LAB_ORDER_SETTINGS': (state, { procedureID, labOrderSettings, }) => {
        var _a, _b;
        let procedures = Object.values((_a = state.draftProcedures) !== null && _a !== void 0 ? _a : {});
        if (procedureID) {
            procedures = [(_b = state.draftProcedures) === null || _b === void 0 ? void 0 : _b[procedureID]].filter(nullishFilter);
        }
        procedures.forEach((inputProcedure) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            const procedure = inputProcedure;
            procedure.billTo = (_a = labOrderSettings.selectedBillingOption) !== null && _a !== void 0 ? _a : procedure.billTo;
            procedure.labPriority = (_b = labOrderSettings.selectedLabProcessingSpeedOption) !== null && _b !== void 0 ? _b : procedure.labPriority;
            procedure.collectionDate = (_c = labOrderSettings.selectedCollectionDateOption) !== null && _c !== void 0 ? _c : procedure.collectionDate;
            procedure.collectionTime = (_d = labOrderSettings.selectedCollectionTimeOption) !== null && _d !== void 0 ? _d : procedure.collectionTime;
            procedure.collectOn = (_e = labOrderSettings.collectOn) !== null && _e !== void 0 ? _e : procedure.collectOn;
            procedure.collectEnd = (_f = labOrderSettings.collectEnd) !== null && _f !== void 0 ? _f : procedure.collectEnd;
            if (!isNullish(labOrderSettings.selectedFastingOption)) {
                const procedureFastingInstructionIndex = (_h = (_g = procedure.instructions) === null || _g === void 0 ? void 0 : _g.findIndex((instruction) => instruction.type === InstructionType.FASTING)) !== null && _h !== void 0 ? _h : -1;
                const instruction = {
                    type: InstructionType.FASTING,
                    instruction: labOrderSettings.selectedFastingOption === Fasting.REQUIRED ? 'Yes' : 'No',
                };
                if (procedureFastingInstructionIndex === -1) {
                    procedure.instructions = [...((_j = procedure.instructions) !== null && _j !== void 0 ? _j : []), instruction];
                }
                else if (procedure.instructions) {
                    procedure.instructions.splice(procedureFastingInstructionIndex, 1, instruction);
                }
            }
            state.draftProcedures = {
                ...state.draftProcedures,
                [procedure.id]: procedure,
            };
        });
    },
    'orderFlow/SET_PROCEDURE_TASK_TYPE': (state, payload) => {
        var _a;
        const { procedureID, taskTypeID } = payload;
        const procedure = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID];
        if (!procedure)
            return;
        procedure.taskTypeID = taskTypeID;
    },
    'orderFlow/SET_PRODUCTS': (state, { procedureID, newProducts }) => {
        var _a;
        const setProductsError = new Error('Unable to set product. Please try again.');
        const procedure = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID];
        if (isNullish(procedure))
            throw setProductsError;
        state.draftProcedures = update(state.draftProcedures, {
            [procedureID]: {
                products: { $set: newProducts },
            },
        });
    },
    'orderFlow/SET_PROVIDER': (state, { provider, bookingID, }) => {
        var _a;
        const booking = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
        if (!booking)
            throw new Error('No booking of this ID exists');
        booking.provider = provider;
    },
    'orderFlow/SET_PROVIDER_ID': (state, { bookingID, providerID, providerType, }) => {
        var _a;
        const booking = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
        if (!booking)
            throw new Error('No booking of this ID exists');
        booking.provider = providerType !== null && providerType !== void 0 ? providerType : booking.provider;
        booking.providerID = providerID;
    },
    'orderFlow/SET_READY_FOR_SCHEDULING': (state, isReady) => {
        if (isNullish(state.draftOrder))
            return;
        state.draftOrder = update(state.draftOrder, {
            readyForScheduling: { $set: isReady },
        });
    },
    'orderFlow/SET_SCHEDULING_USER_ID': (state, id) => {
        if (isNullish(state.draftOrder))
            return;
        state.draftOrder = update(state.draftOrder, {
            schedulingUserID: { $set: id },
        });
    },
    'orderFlow/SET_QUEUED_FOR_SCHEDULING': (state, isQueued) => {
        if (isNullish(state.draftOrder))
            return;
        state.draftOrder = update(state.draftOrder, {
            queuedForScheduling: { $set: isQueued },
        });
    },
};
const orderFlowActions = {
    'orderFlow/addMissingPatientsToOffice': async ({ state, dispatch, getters, rootState }) => {
        var _a, _b, _c;
        const patients = getters['orderFlow/getPatients'];
        const patientIDs = Object.keys(patients !== null && patients !== void 0 ? patients : {});
        const officeID = (_a = state.draftOrder) === null || _a === void 0 ? void 0 : _a.officeID;
        if (!officeID)
            throw Error('Missing office ID');
        // Find patients on the draft order that are missing from the options.
        // Patients may be missing after the office on the order is changed, as
        // the patients may not be on the new office.
        const patientOptionIDs = (_c = (_b = rootState.offices.patientOptionsByOffice[officeID]) === null || _b === void 0 ? void 0 : _b.map((option) => option.id)) !== null && _c !== void 0 ? _c : [];
        const missingPatientIDs = patientIDs.filter((id) => !patientOptionIDs.includes(id));
        const promises = missingPatientIDs.map(async (patientID) => {
            const patient = patients[patientID];
            if (!patient) {
                toastErrorAndReport(`Missing patient ${patientID}`);
                return;
            }
            await dispatch('offices/addPatientToOffice', {
                officeID,
                patient,
            });
        });
        await Promise.all(promises);
    },
    'orderFlow/checkPatientLocationAndUpdateOrder': ({ commit, getters }, patientID) => {
        var _a;
        if (!patientID)
            return;
        const patients = getters['orderFlow/getPatients'];
        const patient = patients[patientID];
        if (!((_a = patient === null || patient === void 0 ? void 0 : patient.info) === null || _a === void 0 ? void 0 : _a.address))
            return;
        /**
         * Google Maps treats Puerto Rico as a "country" in its address components,
         * even though it's technically a U.S. territory.
         * This is actually a known quirk with Google's geocoding API. Puerto Rico
         * gets the country type with the value 'PR' instead of being treated as a
         * state within the United States.
         */
        const isInPuertoRico = getCountryISOByLocation(patient.info.address) === 'PR';
        commit('orderFlow/SET_OFFLINE_SCHEDULING', isInPuertoRico);
    },
    'orderFlow/completeOrder': async function ({ commit, state, getters, rootGetters }, { forceSaveAsDraft, readyForScheduling, queuedForScheduling, skipPostCreationActions }) {
        try {
            const { draftOrder, draftWaypoints } = state;
            if (!draftOrder)
                throw Error('Missing order');
            const navToExitRoute = (id, isDraft) => {
                var _a;
                const route = (_a = rootGetters['orderFlow/getExitRoute']) === null || _a === void 0 ? void 0 : _a.call(rootGetters, { id, isDraft });
                const router = getRouter();
                router.push(route !== null && route !== void 0 ? route : homeRoute());
            };
            const { bookings, procedures } = getters['orderFlow/splitBookingProceduresByProducts'];
            /** isFulfilledByOffice is deprecated */
            const isSpecimenBagMismatch = !!(draftOrder === null || draftOrder === void 0 ? void 0 : draftOrder.isFulfilledByOffice) && !!getters['orderFlow/getSpecimenBagReview'];
            const isOrderDraft = getters['orderFlow/getIsOrderIncomplete'];
            if (isSpecimenBagMismatch || isOrderDraft || forceSaveAsDraft) {
                await saveOrderAsDraft(draftOrder, bookings, procedures, readyForScheduling, queuedForScheduling, false);
            }
            else {
                const draftOrderNotQueued = {
                    ...draftOrder,
                    queuedForScheduling: false,
                    readyForScheduling,
                };
                await createOrder({
                    order: draftOrderNotQueued,
                    bookings,
                    draftWaypoints,
                    procedures,
                });
            }
            if (skipPostCreationActions)
                return;
            // Determine if action is required based on the same logic as the backend
            const isDraftOrder = isSpecimenBagMismatch || isOrderDraft || forceSaveAsDraft;
            const isCanceled = hasStatus(draftOrder.status, OrderStatus.CANCELED);
            const actionRequiredFilter = ((isDraftOrder || !draftOrder.offlineScheduling)
                && !isCanceled) ? ActionRequiredFilter.ACTION_REQUIRED
                : ActionRequiredFilter.NO_ACTION_REQUIRED;
            setOfficeLocalOrderID(commit, {
                orderID: draftOrder.id,
                actionRequiredFilter,
            }, this);
            navToExitRoute(draftOrder.id);
        }
        catch (error) {
            toastErrorAndReport(error);
        }
    },
    'orderFlow/completeOrderPrompt': async (_, { skipPostCreationActions }) => saveOrder(skipPostCreationActions),
    async 'orderFlow/fetchFacilities'({ getters }) {
        if (!isDashboardUser())
            return;
        const placeIDs = getters['orderFlow/getOrderFacilityIDs'];
        // Presence of places module not guaranteed.
        await dispatchOrThrow(this, 'places/fetchPlaces', { placeIDs, includePlaceGroups: true });
    },
    'orderFlow/processCSVImportFile': async (context, { csvImportFile }) => {
        const { commit } = context;
        commit('orderFlow/APPEND_ORDER_CSV_IMPORT_LOG_HEADER', 'Beginning CSV import');
        commit('orderFlow/APPEND_ORDER_CSV_IMPORT_LOG', 'Loading file');
        const importFile = await decryptFile(csvImportFile, false);
        if (!importFile)
            return;
        commit('orderFlow/SET_CURRENT_CSV_IMPORT_LOG_STATUS', { status: 'success' });
        const csvPlainTextTo2DArray = (plainText) => plainText
            .replaceAll('\r', '')
            .split('\n')
            .map((row) => row.split(','));
        commit('orderFlow/SET_ORDER_CSV_IMPORT_FILE', csvImportFile);
        const plainText = await importFile.text();
        const previewGenCsv = csvPlainTextTo2DArray(plainText);
        try {
            await processCSVInput(previewGenCsv, context);
            commit('orderFlow/APPEND_ORDER_CSV_IMPORT_LOG', 'SUCCESS');
        }
        catch (error) {
            commit('orderFlow/APPEND_ORDER_CSV_IMPORT_LOG', 'FAILED');
            toastErrorAndReport(error);
        }
    },
    'orderFlow/initFlowSettings': ({ commit, rootState, state }) => {
        var _a, _b, _c, _d, _e, _f, _g;
        let flowSettings = cloneDeep((_c = (_b = (_a = rootState.office) === null || _a === void 0 ? void 0 : _a.office) === null || _b === void 0 ? void 0 : _b.orderSettings.orderFlowSettings) !== null && _c !== void 0 ? _c : {});
        const officeID = (_d = state.draftOrder) === null || _d === void 0 ? void 0 : _d.officeID;
        if (officeID !== ((_f = (_e = rootState.office) === null || _e === void 0 ? void 0 : _e.office) === null || _f === void 0 ? void 0 : _f.id)) {
            if (officeID) {
                const office = rootState.offices.offices[officeID];
                flowSettings = cloneDeep((_g = office === null || office === void 0 ? void 0 : office.orderSettings.orderFlowSettings) !== null && _g !== void 0 ? _g : flowSettings);
            }
            flowSettings.orderingFormat = OrderFormat.MANUAL;
        }
        commit('orderFlow/SET_FLOW_SETTINGS', flowSettings);
    },
    async 'orderFlow/initFlowForExistingOrder'({ dispatch, commit, rootState }, draftOrderID) {
        var _a, _b, _c, _d;
        commit('orderFlow/RESET_MODULE');
        // Office ID only available in pro. The request works without office ID, but
        // passing it saves a step.
        const proOfficeID = ((_b = (_a = rootState.office) === null || _a === void 0 ? void 0 : _a.office) === null || _b === void 0 ? void 0 : _b.type) === OfficeType.LAB
            ? undefined
            : (_d = (_c = rootState.office) === null || _c === void 0 ? void 0 : _c.office) === null || _d === void 0 ? void 0 : _d.id;
        const { orderFlowStateInfo, draftWaypoints } = await getOrderFlowStateInfoRequest({ draftOrderID });
        if (!orderFlowStateInfo)
            throw Error(`Missing draft order ${draftOrderID}}`);
        const { draftBookings, draftOrder, draftProcedures } = orderFlowStateInfo;
        if (hasMutation(this, 'waypoint/SET_DRAFT_WAYPOINTS')) {
            customCommit(commit, 'waypoint/SET_DRAFT_WAYPOINTS', draftWaypoints);
        }
        const { bookings, procedures, } = mergeBookingProceduresByProducts(draftBookings, draftProcedures);
        orderFlowStateInfo.draftBookings = bookings;
        orderFlowStateInfo.draftProcedures = procedures;
        const officeIdOnOrder = draftOrder === null || draftOrder === void 0 ? void 0 : draftOrder.officeID;
        const officeID = officeIdOnOrder !== null && officeIdOnOrder !== void 0 ? officeIdOnOrder : proOfficeID;
        if (!officeID)
            throw Error('Missing office ID');
        if (isDashboardUser() || isLabOfficeUser()) {
            await dispatch('offices/fetchOffice', { officeID });
        }
        commit('orderFlow/SET_EXISTING_DRAFT_ORDER', orderFlowStateInfo);
        dispatch('orderFlow/initFlowSettings');
        commit('orderFlow/SET_FLOW_DATA_IS_READY', { draftOrderID, ready: true });
    },
    async 'orderFlow/initFlowForNewOrder'({ commit, dispatch, rootState }, { draftOrderID, reOrderID }) {
        var _a, _b, _c, _d;
        commit('orderFlow/RESET_MODULE');
        const { user } = rootState.auth;
        if (!user)
            throw Error('Must be signed in to create orders.');
        const { id: creatorID, role } = user;
        /** CARE-4915: Lab office order flow should have no office selected by default */
        const officeID = !isDashboardUser() && !isLabOfficeUser() ? (_b = (_a = rootState.office) === null || _a === void 0 ? void 0 : _a.office) === null || _b === void 0 ? void 0 : _b.id : undefined;
        if (reOrderID) {
            const { orderFlowStateInfo } = await initNewDraftOrderFromOrderRequest({
                draftOrderID,
                orderID: reOrderID,
            });
            const { bookings: draftBookings, procedures: draftProcedures, } = mergeBookingProceduresByProducts(orderFlowStateInfo.draftBookings, orderFlowStateInfo.draftProcedures);
            orderFlowStateInfo.draftBookings = draftBookings;
            orderFlowStateInfo.draftProcedures = draftProcedures;
            commit('orderFlow/INIT_RE_ORDER', orderFlowStateInfo);
        }
        else {
            commit('orderFlow/INIT_DRAFT_ORDER', { id: draftOrderID, creatorID, officeID });
            commit('orderFlow/ADD_EMPTY_PATIENT_BOOKING', true);
            // Only init if not re-ordering. Re-ordering will use the flow settings
            // from the original order.
            dispatch('orderFlow/initFlowSettings');
        }
        if (!isDashboardUser() && isOfficeLab()) {
            commit('orderFlow/SET_CREATED_BY', creatorID);
            commit('orderFlow/SET_CREATED_BY_OFFICE_ID', (_d = (_c = rootState.office) === null || _c === void 0 ? void 0 : _c.office) === null || _d === void 0 ? void 0 : _d.id);
        }
        if (role === Role.PRESCRIBER) {
            commit('orderFlow/SET_PRESCRIBER_ID_ON_ORDER', creatorID);
        }
        commit('orderFlow/SET_FLOW_DATA_IS_READY', { draftOrderID, ready: true });
    },
    'orderFlow/initProcedure': ({ commit, state, rootState }, { bookingID, taskTypeID }) => {
        var _a, _b, _c, _d, _e, _f, _g;
        updateUserInfoRequest({ warmUp: true });
        if (isNullish(state.draftOrder))
            throw new Error('Unable to create Procedure');
        const officeID = isDashboardUser() ? state.draftOrder.officeID : (_b = (_a = rootState.office) === null || _a === void 0 ? void 0 : _a.office) === null || _b === void 0 ? void 0 : _b.id;
        const office = isDashboardUser()
            ? (officeID && rootState.offices.offices[officeID]) || undefined
            : ((_d = (_c = rootState.office) === null || _c === void 0 ? void 0 : _c.office) !== null && _d !== void 0 ? _d : undefined);
        const draftBooking = (_e = state.draftBookings) === null || _e === void 0 ? void 0 : _e[bookingID];
        const labSettings = (_g = (_f = office === null || office === void 0 ? void 0 : office.orderSettings) === null || _f === void 0 ? void 0 : _f.labSettings) !== null && _g !== void 0 ? _g : undefined;
        const procedure = initDraftProcedure(bookingID, taskTypeID, state.draftOrder.id, labSettings, draftBooking, !isDashboardUser() && isOfficeLab());
        commit('orderFlow/ADD_PROCEDURE', { bookingID, newItem: procedure });
        return procedure;
    },
    'orderFlow/performCompletionAction': async () => {
        const route = getRoute().value;
        await getOrderFlowHelpers(route).performCompletionAction(route);
    },
    'orderFlow/removePatientBooking': ({ state, commit }, bookingID) => {
        var _a, _b, _c;
        const bookingIDs = Object.keys((_b = (_a = state.draftOrder) === null || _a === void 0 ? void 0 : _a.bookings) !== null && _b !== void 0 ? _b : {});
        if (bookingIDs.length === 1) {
            const booking = (_c = state.draftBookings) === null || _c === void 0 ? void 0 : _c[bookingID];
            if (!(booking === null || booking === void 0 ? void 0 : booking.patientID) && !(booking === null || booking === void 0 ? void 0 : booking.procedures)) {
                // The user is attempting to remove the empty placeholder booking.
                toastInfo('There must be at least one patient on the order.');
            }
            // The flow requires at least one booking.
            commit('orderFlow/ADD_EMPTY_PATIENT_BOOKING');
        }
        commit('orderFlow/REMOVE_PATIENT_BOOKING', bookingID);
    },
    'orderFlow/searchAlgoliaICD10Codes': async ({ state, dispatch }, query) => {
        var _a, _b;
        if (isNullish(state.localState.diagnosesKeyData)
            || isNullish(state.localState.diagnosesKeyData.key)
            || needsNewKey(state.localState.diagnosesKeyData)) {
            await dispatch('orderFlow/updateDiagnosticsAlgoliaKey');
        }
        const results = await searchICD10Codes((_b = (_a = state.localState.diagnosesKeyData) === null || _a === void 0 ? void 0 : _a.key) !== null && _b !== void 0 ? _b : '', query, {});
        return results.map(stripAlgoliaProperties);
    },
    'orderFlow/searchAlgoliaPrescribers': async ({ state, dispatch }, payload) => {
        var _a, _b;
        const { query, params } = payload;
        if (isNullish(state.localState.prescriberSearchKeyData)
            || isNullish(state.localState.prescriberSearchKeyData.key)
            || needsNewKey(state.localState.prescriberSearchKeyData)) {
            await dispatch('orderFlow/updatePrescriberSearchAlgoliaKey');
        }
        const results = await searchUsers((_b = (_a = state.localState.prescriberSearchKeyData) === null || _a === void 0 ? void 0 : _a.key) !== null && _b !== void 0 ? _b : '', query, params !== null && params !== void 0 ? params : {});
        return results.map(stripAlgoliaProperties);
    },
    'orderFlow/setDefaultSchedulingUser': async ({ state, commit, rootState }) => {
        var _a, _b, _c, _d, _e, _f;
        const { schedulingParty } = state.data.flowSettings;
        let userID;
        const booking = Object.values((_a = state.draftBookings) !== null && _a !== void 0 ? _a : {})[0];
        const bookingPatientID = booking === null || booking === void 0 ? void 0 : booking.patientID;
        switch (schedulingParty) {
            case OrderParty.PATIENT:
                if (bookingPatientID) {
                    const patient = (_b = rootState.users) === null || _b === void 0 ? void 0 : _b.users[bookingPatientID];
                    const patientCareTeamMember = Object.values((_d = (_c = patient === null || patient === void 0 ? void 0 : patient.careTeam) === null || _c === void 0 ? void 0 : _c[Role.PATIENT]) !== null && _d !== void 0 ? _d : {})[0];
                    if (patientCareTeamMember === null || patientCareTeamMember === void 0 ? void 0 : patientCareTeamMember.id) {
                        userID = patientCareTeamMember.id;
                    }
                    else {
                        userID = bookingPatientID;
                    }
                }
                break;
            case OrderParty.OFFICE:
                userID = (_e = state.data.flowSettings.defaultOfficeSchedulingUserID) !== null && _e !== void 0 ? _e : (_f = rootState.auth.user) === null || _f === void 0 ? void 0 : _f.id;
                break;
        }
        commit('orderFlow/SET_SCHEDULING_USER_ID', userID);
    },
    'orderFlow/searchAlgoliaProducts': async ({ state, dispatch }, params) => {
        var _a, _b, _c, _d;
        if (isNullish(state.localState.testsKeyData)
            || isNullish(state.localState.testsKeyData.key)
            || needsNewKey(state.localState.testsKeyData)) {
            await dispatch('orderFlow/updateTestsAlgoliaKey');
        }
        let results = await searchProducts((_b = (_a = state.localState.testsKeyData) === null || _a === void 0 ? void 0 : _a.key) !== null && _b !== void 0 ? _b : '', (_c = params.query) !== null && _c !== void 0 ? _c : '', {
            filters: (_d = params.filters) !== null && _d !== void 0 ? _d : '',
        });
        /**
         * We currently don't support products that don't have place groups
         * associated, so filter those out of the search results
         */
        results = results.filter((result) => { var _a, _b; return ((_b = Object.values((_a = result.placeGroups) !== null && _a !== void 0 ? _a : {})) === null || _b === void 0 ? void 0 : _b.length) > 0; });
        return results.map(stripAlgoliaProperties);
    },
    'orderFlow/switchOrderFormat': ({ commit }, { asChild, bookingID, orderFormat }) => {
        commit('orderFlow/SET_ORDER_FORMAT_BY_BOOKING_ID', {
            bookingID,
            orderFormat,
        });
        pushAndKeepQuery(orderFlowSwitchFormatRoute(orderFormat, asChild));
    },
    'orderFlow/updateDiagnosticsAlgoliaKey': async ({ commit }) => {
        const keyData = await getAlgoliaAPIKeyForICD10CodesRequest();
        commit('orderFlow/SET_ALGOLIA_DIAGNOSES_KEY', keyData);
    },
    'orderFlow/updatePrescriberSearchAlgoliaKey': async ({ commit }) => {
        const keyData = await getAlgoliaAPIKeyForSearchPrescribersRequest();
        commit('orderFlow/SET_ALGOLIA_PRESCRIBER_SEARCH_KEY', keyData);
    },
    'orderFlow/updateTestsAlgoliaKey': async ({ commit }) => {
        const keyData = await getAlgoliaAPIKeyForProductsRequest();
        commit('orderFlow/SET_ALGOLIA_TESTS_KEY', keyData);
    },
    'orderFlow/setDiagnosesOnProcedure': async ({ commit, dispatch }, { procedureID, diagnoses: icd10CodesSelection }) => {
        try {
            /**
             * At this stage we store diagnosis on the procedure with this format
             *
             * code / code / code
             */
            const instruction = icd10CodesSelection.map((option) => option.label).join(' / ');
            commit('orderFlow/SET_INSTRUCTIONS', {
                procedureID: procedureID,
                instructionUpdate: {
                    instruction: cloneDeep(instruction),
                    type: InstructionType.DIAGNOSIS,
                },
            });
            const diagnoses = [];
            const promises = icd10CodesSelection.map(async (option) => {
                var _a;
                /** Should return the ICD 10 Code with this secret ID */
                const results = (_a = await dispatch('orderFlow/searchAlgoliaICD10Codes', option.id)) !== null && _a !== void 0 ? _a : [];
                if (!results[0] || !results[0].secretID)
                    return;
                diagnoses.push({
                    secretID: await decryptData(results[0].secretID),
                    icd10Code: await encryptData(results[0]),
                });
            });
            await Promise.all(promises);
            /**
             * Store on the draft procedure, these will be copied to the patient in cloud
             * on order creation
             */
            commit('orderFlow/SET_DIAGNOSES_ON_PROCEDURE', {
                procedureID,
                diagnoses: cloneDeep(diagnoses),
            });
        }
        catch (error) {
            toastErrorAndReport(error);
        }
    },
};
const orderFlowGetters = {
    'orderFlow/getAllBookingsOnOrder': (state) => {
        var _a, _b;
        const bookingIDs = Object.keys((_b = (_a = state.draftOrder) === null || _a === void 0 ? void 0 : _a.bookings) !== null && _b !== void 0 ? _b : {});
        const bookings = bookingIDs
            .map((bookingID) => { var _a; return (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID]; })
            .filter(nullishFilter);
        return bookings;
    },
    'orderFlow/getOrderFacilities': (state, getters, rootState) => {
        const placeIDs = orderFlowGetters['orderFlow/getOrderFacilityIDs'](state, getters, rootState);
        return placeIDs.map((placeID) => { var _a, _b; return (_b = (_a = rootState.places) === null || _a === void 0 ? void 0 : _a.places) === null || _b === void 0 ? void 0 : _b[placeID]; })
            .filter(nullishFilter);
    },
    'orderFlow/getOrderFlowHelpers': () => (route) => getOrderFlowHelpers(route),
    'orderFlow/getBackStepRouteInOrder': () => (route) => {
        const back = orderFlowGetters['orderFlow/getOrderFlowHelpers']()(route)
            .getBackOrNextStepRoute(route, 'back');
        if (!back)
            return null;
        return keepParams(keepQuery(back));
    },
    'orderFlow/getCurrentStep': () => (route) => orderFlowGetters['orderFlow/getOrderFlowHelpers']()(route).getStepFromRoute(route),
    'orderFlow/getCurrentStepCompletionAction': () => (route) => orderFlowGetters['orderFlow/getOrderFlowHelpers']()(route).getCurrentStepCompletionAction(route),
    'orderFlow/getCurrentStepSecondaryComponent': () => (route) => orderFlowGetters['orderFlow/getOrderFlowHelpers']()(route).getCurrentStepSecondaryHeaderComponent(route),
    'orderFlow/getCurrentStepTitle': () => (route) => orderFlowGetters['orderFlow/getOrderFlowHelpers']()(route).getCurrentStepTitle(route),
    'orderFlow/getCurrentStepValidationFailures': () => (route) => orderFlowGetters['orderFlow/getOrderFlowHelpers']()(route).getCurrentStepValidationFailures(route),
    'orderFlow/getDraftBookingProcedures': (state) => (bookingID) => {
        var _a, _b, _c;
        const booking = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
        if (isNullish(booking))
            throw new Error('Booking not found');
        const procedureIDs = Object.keys((_b = booking.procedures) !== null && _b !== void 0 ? _b : {});
        const procedures = Object.values((_c = state.draftProcedures) !== null && _c !== void 0 ? _c : {})
            .filter((procedure) => procedureIDs.includes(procedure.id));
        return procedures;
    },
    /**
     * Returns the facility IDs that are already associated with **every** patient
     * on the order. (Facility ID included on only some patients excluded)
     */
    'orderFlow/getFacilityIDsOnPatients': (state, getters, rootState) => {
        const patients = Object.values(orderFlowGetters['orderFlow/getPatients'](state, getters, rootState));
        const facilityIDsNestedArray = patients.map((patient) => {
            var _a;
            const patientPlaces = Object.values((_a = patient.places) !== null && _a !== void 0 ? _a : {})
                .filter((place) => place.placeRoles[UserPlaceRole.PATIENT]);
            return patientPlaces.map((place) => place.id);
        });
        return getIntersection(facilityIDsNestedArray);
    },
    'orderFlow/getFlowSettings': (state) => state.data.flowSettings,
    'orderFlow/getIsCurrentStepValid': () => (route) => computed(() => orderFlowGetters['orderFlow/getCurrentStepValidationFailures']()(route) === null),
    /**
     * Returns data on mismatches between specimen bags and procedures.
     *
     * Returns `undefined` if:
     * - No specimen bags have previously been charted, or
     * - There are no mismatches found, or
     * - The order will be fulfilled by CareSend
     */
    'orderFlow/getSpecimenBagReview': (state) => {
        var _a, _b;
        if (!((_a = state.draftOrder) === null || _a === void 0 ? void 0 : _a.isFulfilledByOffice))
            return undefined;
        const specimenBags = Object.values((_b = state.draftBookings) !== null && _b !== void 0 ? _b : {})
            .reduce((acc, booking) => {
            if (!booking.specimenBags)
                return acc;
            return {
                ...acc,
                ...booking.specimenBags,
            };
        }, {});
        if (!Object.keys(specimenBags !== null && specimenBags !== void 0 ? specimenBags : {}).length)
            return undefined;
        const { procedures, } = orderFlowGetters['orderFlow/splitBookingProceduresByProducts'](state);
        const specimenBagTasksWithNoProcedureMatch = Object.keys(specimenBags !== null && specimenBags !== void 0 ? specimenBags : {})
            .reduce((acc, taskID) => {
            const procedure = Object.values(procedures !== null && procedures !== void 0 ? procedures : {}).find((draftProcedure) => draftProcedure.taskID === taskID);
            if (!procedure || !procedure.specimenBag) {
                return {
                    ...acc,
                    [taskID]: true,
                };
            }
            return acc;
        }, {});
        const procedureTasksWithNoSpecimenBagMatch = Object.values(procedures !== null && procedures !== void 0 ? procedures : {})
            .reduce((acc, procedure) => {
            if (!procedure.specimenBag && procedure.taskID) {
                return {
                    ...acc,
                    [procedure.taskID]: true,
                };
            }
            return acc;
        }, {});
        if (Object.keys(specimenBagTasksWithNoProcedureMatch !== null && specimenBagTasksWithNoProcedureMatch !== void 0 ? specimenBagTasksWithNoProcedureMatch : {}).length === 0
            && Object.keys(procedureTasksWithNoSpecimenBagMatch !== null && procedureTasksWithNoSpecimenBagMatch !== void 0 ? procedureTasksWithNoSpecimenBagMatch : {}).length === 0)
            return;
        return {
            specimenBagTasksWithNoProcedureMatch,
            procedureTasksWithNoSpecimenBagMatch,
        };
    },
    'orderFlow/getNextStepRouteInOrder': () => (route) => {
        const next = orderFlowGetters['orderFlow/getOrderFlowHelpers']()(route)
            .getBackOrNextStepRoute(route, 'next');
        if (!next)
            return null;
        return keepParams(keepQuery(next));
    },
    'orderFlow/getOrderFacilityIDs': (state, getters, rootState) => {
        var _a;
        const { drawPlaceID } = (_a = state.draftOrder) !== null && _a !== void 0 ? _a : {};
        const patientFacilityIDs = orderFlowGetters['orderFlow/getFacilityIDsOnPatients'](state, getters, rootState);
        const desiredIDs = [...patientFacilityIDs, drawPlaceID].filter(nullishFilter);
        return deduplicateArray(desiredIDs);
    },
    'orderFlow/getPatientInfoByID': (_state, _getters, rootState) => (patientID) => {
        var _a;
        let patient = null;
        let isNew = true;
        if (patientID) {
            const officePatient = (_a = rootState.users) === null || _a === void 0 ? void 0 : _a.users[patientID];
            if (officePatient) {
                // TODO: structureUpdate - fix and remove typecast
                patient = initExistingPatientForEditing(officePatient);
                isNew = false;
            }
        }
        return {
            patient,
            isNew,
        };
    },
    'orderFlow/getPatientInfoOnBooking': (state, getters, rootState) => (bookingID) => {
        var _a;
        const booking = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
        if (isNullish(booking))
            throw new Error('Booking not found');
        const { patientID } = booking;
        return orderFlowGetters['orderFlow/getPatientInfoByID'](state, getters, rootState)(patientID);
    },
    'orderFlow/getPatients': (state, _getters, rootState) => {
        var _a;
        const patientIDs = Object.values((_a = state.draftBookings) !== null && _a !== void 0 ? _a : {})
            .map((booking) => booking.patientID)
            .filter(nullishFilter);
        const patients = patientIDs.map((patientID) => {
            var _a;
            const patient = (_a = rootState.users) === null || _a === void 0 ? void 0 : _a.users[patientID];
            return patient;
        }).filter(nullishFilter);
        return arrayToObj(patients, 'id');
    },
    'orderFlow/getIsBookingIncomplete': (state) => (bookingID) => {
        var _a, _b;
        const booking = (_a = state.draftBookings) === null || _a === void 0 ? void 0 : _a[bookingID];
        // TODO: Explain reasoning for returning false here or consider changing to
        // throw error.
        if (!booking)
            return false;
        if (!booking.patientID
            || !Object.values((_b = booking.procedures) !== null && _b !== void 0 ? _b : {}).length)
            return true;
        return false;
    },
    'orderFlow/getIsProcedureIncomplete': (state) => (procedureID) => {
        var _a, _b, _c, _d;
        const procedure = (_a = state.draftProcedures) === null || _a === void 0 ? void 0 : _a[procedureID];
        // TODO: Explain reasoning for returning false here or consider changing to
        // throw error.
        if (!procedure)
            return false;
        const { draftOrder } = state;
        if (!(draftOrder === null || draftOrder === void 0 ? void 0 : draftOrder.officeID)
            || !(draftOrder === null || draftOrder === void 0 ? void 0 : draftOrder.prescriberID)
            || !((_b = procedure.diagnoses) === null || _b === void 0 ? void 0 : _b.length)
            || !Object.values((_c = procedure.products) !== null && _c !== void 0 ? _c : {}).length
            || !Object.values((_d = procedure.placeAccounts) !== null && _d !== void 0 ? _d : {}).length
            || !procedure.billTo
            || !procedure.collectionDate
            || !procedure.collectionTime
            || !procedure.labPriority)
            return true;
        if (!procedure.bookingID)
            throw new Error('Procedure must have a booking ID');
        return orderFlowGetters['orderFlow/getIsBookingIncomplete'](state)(procedure.bookingID);
    },
    /**
     * Returns `true` if the order is ineligible to be saved as a full order.
     */
    'orderFlow/getIsOrderIncomplete': (state) => {
        var _a, _b;
        const { draftOrder } = state;
        // An order fulfilled by CareSend must have a scheduling user, unless it is
        // associated with a draw place (facility).
        if (!(draftOrder === null || draftOrder === void 0 ? void 0 : draftOrder.schedulingUserID)
            && !(draftOrder === null || draftOrder === void 0 ? void 0 : draftOrder.isFulfilledByOffice)
            && !(draftOrder === null || draftOrder === void 0 ? void 0 : draftOrder.drawPlaceID))
            return true;
        // Orders must have at least one booking
        if (!Object.values((_a = draftOrder.bookings) !== null && _a !== void 0 ? _a : {}).length)
            return true;
        // Orders must have an office and prescriber selected
        if (!draftOrder.officeID
            || !draftOrder.prescriberID)
            return true;
        // In-office orders must be compatible with previously entered specimen
        // bags.
        const hasSpecimenBagMismatch = !!orderFlowGetters['orderFlow/getSpecimenBagReview'](state);
        if (hasSpecimenBagMismatch)
            return true;
        const isSomeBookingIncomplete = Object.values((_b = draftOrder.bookings) !== null && _b !== void 0 ? _b : {})
            .some((booking) => orderFlowGetters['orderFlow/getIsBookingIncomplete'](state)(booking.id)
            || orderFlowGetters['orderFlow/getDraftBookingProcedures'](state)(booking.id)
                .some((procedure) => orderFlowGetters['orderFlow/getIsProcedureIncomplete'](state)(procedure.id)));
        return isSomeBookingIncomplete;
    },
    'orderFlow/splitBookingProceduresByProducts': (state) => splitBookingProceduresByProducts(state.draftBookings, state.draftProcedures),
    'orderFlow/mergeBookingProceduresByProducts': (state) => mergeBookingProceduresByProducts(state.draftBookings, state.draftProcedures),
    'orderFlow/getIsOrderScheduleFriendly': (state) => {
        var _a, _b;
        try {
            if ((_a = state.draftOrder) === null || _a === void 0 ? void 0 : _a.isFulfilledByOffice)
                return false;
            const { bookings, procedures, } = orderFlowGetters['orderFlow/splitBookingProceduresByProducts'](state);
            const allBookingsHavePatients = Object.values(bookings !== null && bookings !== void 0 ? bookings : {})
                .every((booking) => !!booking.patientID);
            if (!allBookingsHavePatients)
                return false;
            const allProceduresHaveProducts = Object.values(procedures).every((procedure) => { var _a; return Object.values((_a = procedure.products) !== null && _a !== void 0 ? _a : {}).length > 0; });
            if (!allProceduresHaveProducts)
                return false;
            const orderHasPrescriber = !!((_b = state.draftOrder) === null || _b === void 0 ? void 0 : _b.prescriberID);
            if (!orderHasPrescriber)
                return false;
            return true;
        }
        catch (error) {
            return false;
        }
    },
};
const initOrderFlowWatches = (store) => {
    // Fetch office dependencies when officeID changes. Copy patients to the new
    // office as needed.
    store.watch((state) => { var _a; return (_a = state.orderFlow.draftOrder) === null || _a === void 0 ? void 0 : _a.officeID; }, async (officeID) => {
        var _a, _b, _c;
        if (!officeID)
            return;
        try {
            store.commit('orderFlow/SET_OFFICE_DEPENDENCIES_LOADING', true);
            // If the office has changed, all offices are already loaded if Dashboard user via
            // `fetchAllOfficesAsSuperAdmin`. Only fetch dependencies is required if lab user here.
            await store.dispatch('offices/fetchOffice', {
                officeID,
            });
            await store.dispatch('orderFlow/initFlowSettings');
            /** Update lab order settings for all procedures if office is changed */
            const labOrderSettings = (_c = (officeID
                ? (_b = (_a = store.state.offices.offices[officeID]) === null || _a === void 0 ? void 0 : _a.orderSettings) === null || _b === void 0 ? void 0 : _b.labSettings : undefined)) !== null && _c !== void 0 ? _c : {};
            const procedures = store.state.orderFlow.draftProcedures;
            Object.values(procedures !== null && procedures !== void 0 ? procedures : {}).forEach((procedure) => {
                store.commit('orderFlow/SET_PROCEDURE_LAB_ORDER_SETTINGS', {
                    procedureID: procedure.id,
                    labOrderSettings,
                });
            });
            await store.dispatch('orderFlow/addMissingPatientsToOffice');
        }
        catch (error) {
            toastErrorAndReport(error);
        }
        store.commit('orderFlow/SET_OFFICE_DEPENDENCIES_LOADING', false);
    });
    // Clear scheduling-related data when offlineScheduling becomes true
    store.watch((state) => { var _a; return (_a = state.orderFlow.draftOrder) === null || _a === void 0 ? void 0 : _a.offlineScheduling; }, (offlineScheduling) => {
        if (offlineScheduling) {
            // Clear scheduling user ID since scheduling will be handled offline
            store.commit('orderFlow/SET_SCHEDULING_USER_ID', undefined);
            // Clear draw place ID since facility selection is no longer relevant
            store.commit('orderFlow/SET_DRAW_PLACE_ID', undefined);
        }
    });
};
const orderFlowModule = {
    state: orderFlowState,
    mutations: orderFlowMutations,
    actions: orderFlowActions,
    getters: orderFlowGetters,
};
const initOrderFlowModule = (moduleExtensions) => initExtendedModule(orderFlowModule, moduleExtensions);

export { initOrderFlowModule, initOrderFlowWatches };
